import React from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { types as sdkTypes } from '../../util/sdkLoader';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { displayPrice } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { formatMoney } from '../../util/currency';
import { ensureListing, ensureUser } from '../../util/data';
import { richText } from '../../util/richText';
import {
  canonicalListingPathFromListing,
  hasThaiListingTranslation,
} from '../../util/canonicalUrls';
import { createResourceLocatorString } from '../../util/routes';
import { isBookingProcessAlias } from '../../transactions/transaction';
import { getListingReviewSummary } from '../../util/listingReviewSummary';
import { updateProfile } from '../../containers/ProfileSettingsPage/ProfileSettingsPage.duck';
import VirtualTourCardBadge from '../listing/VirtualTourCardBadge';
import { getNewListingReferenceDate, isNewListing } from '../../util/newListings';

import { AspectRatioWrapper, Icons, NamedLink, ResponsiveImage } from '../../components';

import css from './ListingCard.module.css';
import {
  getEffectiveLocale,
  getLocalizedListingData,
  getLowestPrice,
  getSinglePricingLabel,
} from '../../util/genericHelpers';
const { UUID, Money } = sdkTypes;

const MIN_LENGTH_FOR_LONG_WORDS = 10;

export { getListingReviewSummary } from '../../util/listingReviewSummary';
export { getNewListingReferenceDate, isNewListing } from '../../util/newListings';

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const NewListingBadge = props => {
  const { createdAt } = props;

  return isNewListing(createdAt) ? <span className={css.newListingBadge}>New Listing</span> : null;
};

export const getNextFavorites = (favorites, listingId) => {
  const favoriteIds = Array.isArray(favorites) ? favorites : [];
  return favoriteIds.includes(listingId)
    ? favoriteIds.filter(favoriteId => favoriteId !== listingId)
    : [...favoriteIds, listingId];
};

const FavoriteButton = props => {
  const { currentUser, intl, listingId } = props;
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const routes = useRouteConfiguration();
  const storedFavorites = currentUser?.attributes?.profile?.privateData?.favorites;
  const favorites = Array.isArray(storedFavorites) ? storedFavorites : [];
  const isFavorite = favorites.includes(listingId);
  const ariaLabel = intl.formatMessage({
    id: isFavorite ? 'ListingCard.removeFromFavorites' : 'ListingCard.addToFavorites',
  });

  const handleClick = event => {
    event.preventDefault();
    event.stopPropagation();

    if (!currentUser) {
      const from = `${location.pathname}${location.search}${location.hash}`;
      history.push(createResourceLocatorString('SignupPage', routes, {}, {}), { from });
      return;
    }

    dispatch(updateProfile({ privateData: { favorites: getNextFavorites(favorites, listingId) } }));
  };

  return (
    <button
      type="button"
      className={css.favoriteButton}
      aria-label={ariaLabel}
      aria-pressed={isFavorite}
      onClick={handleClick}
    >
      <Icons name={isFavorite ? 'favorite' : 'unfavorite'} />
    </button>
  );
};

const ListingCardRating = props => {
  const { intl, reviewSummary } = props;

  if (!reviewSummary) {
    return null;
  }

  const { averageRating, reviewCount } = reviewSummary;
  const formattedRating = averageRating.toFixed(1);
  const formattedCount = intl.formatNumber(reviewCount, {
    maximumFractionDigits: 0,
  });

  return (
    <div
      className={css.ratings}
      aria-label={`${formattedRating} rating from ${formattedCount} reviews`}
    >
      <Icons name="star" />
      <span>
        {formattedRating} ({formattedCount})
      </span>
    </div>
  );
};

const shouldShowPrice = (price, publicData, config) => {
  const { listingType } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);
  return !(!showPrice && price);
};

const PriceMaybe = props => {
  const { price, publicData, config, intl } = props;
  const { advancePricingDetails } = publicData || {};
  const startingPrice = getLowestPrice(
    advancePricingDetails?.pricingMatrices,
    publicData?.bookingType
  );
  const startingPriceMoneyFormat = startingPrice > 0 && new Money(startingPrice, config?.currency);
  if (!shouldShowPrice(price, publicData, config)) {
    return null;
  }

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(startingPriceMoneyFormat, config.currency, intl);
  const pricingLabel = getSinglePricingLabel(
    advancePricingDetails?.pricingMatrices,
    publicData?.bookingType
  );

  return (
    <div className={css.price}>
      <div className={css.priceValue} title={priceTitle}>
        {/* {formattedPrice} */}
        <>
          <FormattedMessage id="ListingCard.formPriceText" />
          {pricingLabel ? ` ${pricingLabel}` : null}
        </>
      </div>
    </div>
  );
};

/**
 * ListingCard
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.listing API entity: listing or ownListing
 * @param {string?} props.renderSizes for img/srcset
 * @param {Function?} props.setActiveListing
 * @param {boolean?} props.showAuthorInfo
 * @returns {JSX.Element} listing card to be used in search result panel etc.
 */
export const ListingCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();
  const {
    className,
    rootClassName,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo = true,
    hasVirtualTour = false,
  } = props;
  const { currentUser } = useSelector(state => state.user);

  // getEffectiveLocale now handles both logged-in (from user profile)
  // and non-logged-in users (from localStorage)
  const locale = intl?.locale || getEffectiveLocale(currentUser);

  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { price } = currentListing.attributes;
  const createdAt = getNewListingReferenceDate(currentListing);
  const reviewSummary =
    props.reviewSummary !== undefined
      ? props.reviewSummary
      : getListingReviewSummary(currentListing);
  const { title, description, publicData } = getLocalizedListingData(currentListing, locale);
  const showPriceAndRating = shouldShowPrice(price, publicData, config) || !!reviewSummary;

  const author = ensureUser(listing.author);
  const authorName = author.attributes.profile.displayName;
  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'scaled-xlarge',
  } = config.layout.listingImage;

  const hasDefault = !!firstImage?.attributes?.variants?.['default']?.url;

  const variants = hasDefault
    ? ['default']
    : Object.keys(firstImage?.attributes?.variants || {}).filter(k =>
        k.startsWith(config.layout.listingImage.variantPrefix || 'scaled-xlarge')
      );

  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  // Build href manually for <a> to avoid prefetching of <NamedLink>
  const linkLocale = locale === 'th' && hasThaiListingTranslation(currentListing) ? 'th' : 'en';
  const href = canonicalListingPathFromListing(currentListing, linkLocale) || `/l/${id}`;

  return (
    <div className={classes}>
      <a className={css.cardLink} href={href} data-search-listing-id={id}>
        <AspectRatioWrapper
          className={css.aspectRatioWrapper}
          width={aspectWidth}
          height={aspectHeight}
          {...setActivePropsMaybe}
        >
          <LazyImage
            rootClassName={css.rootForImage}
            alt={title}
            image={firstImage}
            variants={variants}
            sizes={renderSizes}
          />
          <NewListingBadge createdAt={createdAt} />
          {hasVirtualTour ? <VirtualTourCardBadge /> : null}
        </AspectRatioWrapper>
        <div className={css.info}>
          {showPriceAndRating ? (
            <div className={css.priceAndRating}>
              <PriceMaybe price={price} publicData={publicData} config={config} intl={intl} />
              <ListingCardRating intl={intl} reviewSummary={reviewSummary} />
            </div>
          ) : null}
          <div className={css.mainInfo}>
            <div className={css.titleRow}>
              <div className={css.title}>
                {richText(title, {
                  longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
                  longWordClass: css.longWord,
                })}
              </div>
            </div>
          </div>
        </div>
      </a>
      <FavoriteButton currentUser={currentUser} intl={intl} listingId={id} />
    </div>
  );
};

export default ListingCard;
